import type { Criticality } from '@/lib/criticality';
export interface CertificationGap {
  equipmentId: string;
  assetNumber: string;
  name: string;
  className: string;
  locationName: string | null;
  /** Resolved consequence of failure, with where the figure came from. */
  criticality: Criticality;
  missing: string[];
  lapsed: { type: string; certificateNumber: string; expiredOn: string }[];
  expiringSoon: { type: string; certificateNumber: string; expiresOn: string }[];
}

export interface CertificationGapSummary {
  assetsWithGaps: number;
  missingCount: number;
  lapsedCount: number;
  expiringSoonCount: number;
}

import { api } from '../client';

export type EquipmentCategory =
  'DRILLING' | 'COMPLETION' | 'PRODUCTION' | 'TESTING' | 'SAFETY' | 'TRANSPORTATION' | 'OTHER';

export type EquipmentStatus =
  'AVAILABLE' | 'IN_USE' | 'MAINTENANCE' | 'REPAIR' | 'RETIRED' | 'DISPOSED';

export type EquipmentCondition = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'CRITICAL';

export type DowntimeReason =
  | 'SCHEDULED_MAINTENANCE'
  | 'UNSCHEDULED_MAINTENANCE'
  | 'BREAKDOWN'
  | 'INSPECTION'
  | 'CALIBRATION'
  | 'OPERATOR_ERROR'
  | 'ENVIRONMENTAL'
  | 'OTHER';

export interface Equipment {
  id: string;
  companyId: string;
  assetNumber: string;
  name: string;
  description: string | null;
  category: EquipmentCategory;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  status: EquipmentStatus;
  condition: EquipmentCondition;
  purchaseDate: string | null;
  purchasePrice: number | null;
  warrantyExpiry: string | null;
  lastServiceDate: string | null;
  nextServiceDate: string | null;
  operatingHours: number | null;
  // Phase 1 Enhanced: Equipment Hierarchy
  parentId: string | null;
  level: number;
  fullPath: string | null;
  lastMeterUpdate: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    workOrders: number;
    downtimeEvents: number;
    maintenanceSchedules: number;
    children?: number;
  };
  /** Resolved consequence of failure, with where the figure came from. */
  criticality: Criticality;
  /** Null when nothing is missing, lapsed or falling due. */
  certification: {
    missing: number;
    lapsed: number;
    expiringSoon: number;
    /** Cannot legally go out. Expiring is a warning, not a bar. */
    blocking: number;
  } | null;
  /** The job it is out on right now, or null if it is with us. */
  deployedTo: { id: string; jobNumber: string } | null;
}

export interface EquipmentDetail extends Equipment {
  parent?: {
    id: string;
    name: string;
    assetNumber: string;
    category: EquipmentCategory;
    level: number;
  } | null;
  children?: Array<{
    id: string;
    name: string;
    assetNumber: string;
    category: EquipmentCategory;
    status: EquipmentStatus;
    level: number;
    children?: Array<{
      id: string;
      name: string;
      assetNumber: string;
      category: EquipmentCategory;
      status: EquipmentStatus;
      level: number;
    }>;
  }>;
  workOrders: {
    id: string;
    workOrderNumber: string;
    title: string;
    type: string;
    status: string;
    scheduledStart: string | null;
    actualEnd: string | null;
  }[];
  downtimeEvents: DowntimeEvent[];
  maintenanceSchedules: EquipmentMaintenanceSchedule[];
  certificates: {
    id: string;
    certificate: {
      id: string;
      certificateNumber: string;
      type: string;
      name: string;
      expiryDate: string | null;
    };
  }[];
}

export interface DowntimeEvent {
  id: string;
  equipmentId: string;
  startTime: string;
  endTime: string | null;
  reason: DowntimeReason;
  description: string | null;
  workOrderId: string | null;
  workOrder?: {
    id: string;
    workOrderNumber: string;
    title: string;
  };
  reportedById: string;
  reportedBy: {
    id: string;
    fullName: string;
  };
  cost: number | null;
  createdAt: string;
  updatedAt: string;
}

interface EquipmentMaintenanceSchedule {
  id: string;
  name: string;
  frequency: string;
  nextDue: string;
  priority: string;
  status: string;
}

export interface CreateEquipmentDto {
  assetNumber: string;
  name: string;
  description?: string;
  category: EquipmentCategory;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  locationId: string;
  parentId?: string; // Phase 1 Enhanced: Equipment Hierarchy
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  lastServiceDate?: string;
  nextServiceDate?: string;
  operatingHours?: number;
}

export interface UpdateEquipmentDto extends Partial<CreateEquipmentDto> {}

export interface RecordDowntimeDto {
  startTime: string;
  endTime?: string;
  reason: DowntimeReason;
  description?: string;
  workOrderId?: string;
  cost?: number;
}

export interface EndDowntimeDto {
  endTime?: string;
  cost?: number;
}

export interface QueryEquipmentParams {
  category?: EquipmentCategory;
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  locationId?: string;
  search?: string;
  maintenanceDue?: boolean;
  page?: number;
  limit?: number;
}

export interface EquipmentDashboard {
  total: number;
  byStatus: Record<EquipmentStatus, number>;
  byCondition: Record<EquipmentCondition, number>;
  maintenanceDue: number;
  utilizationRate: number;
  recentDowntime: {
    id: string;
    startTime: string;
    endTime: string | null;
    reason: DowntimeReason;
    equipment: {
      name: string;
      assetNumber: string;
    };
  }[];
}

export interface EquipmentAnalytics {
  mttr: number;
  totalDowntimeEvents: number;
  topDowntime: {
    equipmentId: string;
    name: string;
    hours: number;
    count: number;
  }[];
  byReason: Record<DowntimeReason, number>;
}

interface EquipmentPaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Phase 1 Enhanced: Equipment Hierarchy Types
export interface EquipmentTreeNode {
  id: string;
  companyId: string;
  assetNumber: string;
  name: string;
  category: EquipmentCategory;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  level: number;
  operatingHours: number | null;
  location: {
    id: string;
    name: string;
    code: string;
  };
  children: EquipmentTreeNode[];
  _count: {
    children: number;
    workOrders: number;
  };
}

export interface EquipmentBreadcrumb {
  id: string;
  name: string;
  level: number;
}

export interface UpdateMetersDto {
  hoursUsed: number;
}

/** Fields a bulk edit may set. Mirrors BulkEquipmentChangesDto on the server. */
export interface BulkEquipmentChanges {
  status?: string;
  condition?: string;
  category?: string;
  locationId?: string;
  nextServiceDate?: string;
}

export interface BulkUpdateEquipmentRequest {
  equipmentIds: string[];
  changes: BulkEquipmentChanges;
}

export interface BulkUpdateEquipmentRowResult {
  id: string;
  assetNumber?: string;
  name?: string;
  status: 'updated' | 'failed';
  reason?: string;
  [key: string]: unknown;
}

/** A bulk edit applies per asset, so the response reports per asset. */
export interface BulkUpdateEquipmentResult {
  requested: number;
  updated: number;
  failed: number;
  fields: string[];
  results: BulkUpdateEquipmentRowResult[];
}

export const equipmentApi = {
  getAll: (params?: QueryEquipmentParams) =>
    api.get<EquipmentPaginatedResponse<Equipment>>('/equipment', { params }),

  getById: (id: string) => api.get<EquipmentDetail>(`/equipment/${id}`),

  getDashboard: () => api.get<EquipmentDashboard>('/equipment/dashboard'),

  getAnalytics: () => api.get<EquipmentAnalytics>('/equipment/analytics'),

  create: (data: CreateEquipmentDto) => api.post<Equipment>('/equipment', data),

  update: (id: string, data: UpdateEquipmentDto) => api.patch<Equipment>(`/equipment/${id}`, data),

  bulkUpdate: async (data: BulkUpdateEquipmentRequest) => {
    const response = await api.post<BulkUpdateEquipmentResult>('/equipment/bulk/update', data);
    return response.data;
  },

  delete: (id: string) => api.delete<{ success: boolean }>(`/equipment/${id}`),

  // Downtime management
  getDowntimeHistory: (equipmentId: string) =>
    api.get<DowntimeEvent[]>(`/equipment/${equipmentId}/downtime`),

  recordDowntime: (equipmentId: string, data: RecordDowntimeDto) =>
    api.post<DowntimeEvent>(`/equipment/${equipmentId}/downtime`, data),

  endDowntime: (equipmentId: string, eventId: string, data: EndDowntimeDto) =>
    api.patch<DowntimeEvent>(`/equipment/${equipmentId}/downtime/${eventId}`, data),

  // Phase 1 Enhanced: Equipment Hierarchy
  getTree: () => api.get<EquipmentTreeNode[]>('/equipment/tree/all'),

  getWithHierarchy: (id: string) => api.get<EquipmentDetail>(`/equipment/${id}/hierarchy`),

  getBreadcrumbs: (id: string) => api.get<EquipmentBreadcrumb[]>(`/equipment/${id}/breadcrumbs`),

  updateMeters: (id: string, data: UpdateMetersDto) =>
    api.patch<Equipment>(`/equipment/${id}/meters`, data),
  /** Assets missing, or about to lose, a certificate their class requires. */
  async getCertificationGaps(horizonDays?: number) {
    const response = await api.get<CertificationGap[]>('/equipment/certification-gaps', {
      params: horizonDays ? { horizonDays } : undefined,
    });
    return response.data;
  },

  async getCertificationGapSummary() {
    const response = await api.get<CertificationGapSummary>(
      '/equipment/certification-gaps/summary'
    );
    return response.data;
  },
};
